import { svgLogoMediumSize } from '@shared/assets/icons/svgs';
import { Text } from '@shared/components';
import { ChatViewContextGetter } from '@shared/contexts/ChatWindowContext';
import { useConciergeContext } from '@shared/plugin/contexts/ConciergeContext';
import type { ChatViewContext } from 'clerk_common';
import clsx from 'clsx';
import { ReactNode, useRef } from 'react';
import { useChatSuggestions } from '../hooks/useChatSuggestions';
import { ChatSuggestion } from '../utils/chatSuggestions';
import {
  ChatMessageInput,
  MessageDraft,
  saveNewChatDraft,
} from './ChatMessageInput';
import { ChatSuggestions } from './ChatSuggestions';
import { ThreadHeader } from './ThreadHeader';

type NewChatPanelProps = {
  getSuggestionContext?: ChatViewContextGetter;
  getViewContext?: ChatViewContextGetter;
  hasTabCompletion?: boolean;
  inputBanner?: ReactNode;
  isCompact?: boolean;
  leadingAction?: ReactNode;
  onBack?: () => void;
  onConversationStarted: (conversationId: string, draft: MessageDraft) => void;
  trailingAction?: ReactNode;
};

export function NewChatPanel({
  getSuggestionContext,
  getViewContext,
  hasTabCompletion = true,
  inputBanner,
  isCompact = false,
  leadingAction,
  onBack,
  onConversationStarted,
  trailingAction,
}: NewChatPanelProps) {
  const { createNewConversation, organizationId } = useConciergeContext();
  const { suggestions } = useChatSuggestions({
    getSuggestionContext,
    organizationId,
  });
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const createConversationWithViewContext = (viewContext?: ChatViewContext) =>
    createNewConversation(viewContext ?? { entityIds: {} });

  function draftSuggestion({ message }: ChatSuggestion) {
    if (inputRef.current?.readOnly) return;
    saveNewChatDraft(organizationId, message);
    inputRef.current?.focus();
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {(onBack || leadingAction || trailingAction) && (
        <ThreadHeader
          isCompact={isCompact}
          leadingAction={leadingAction}
          onBack={onBack}
          trailingAction={trailingAction}
        />
      )}
      <div
        className={clsx(
          'flex flex-1 flex-col items-center justify-center gap-10',
          isCompact ? 'px-4 pb-4' : 'px-20 pb-6'
        )}
      >
        <div className="flex items-center gap-3">
          <span aria-hidden className="[&_svg]:h-8 [&_svg]:w-8">
            {svgLogoMediumSize}
          </span>
          <Text type="h5" className="text-dark">
            What can Vee help with?
          </Text>
        </div>
        <div className="relative flex w-full max-w-3xl flex-col gap-2">
          {inputBanner}
          <div className="rounded-lg shadow-lg">
            <ChatMessageInput
              conversationId={null}
              createConversation={createConversationWithViewContext}
              getViewContext={getViewContext}
              hasMirrorableExternalChannel={false}
              inputRef={inputRef}
              organizationId={organizationId}
              tabCompletion={
                hasTabCompletion ? suggestions[0]?.message : undefined
              }
              onConversationStarted={onConversationStarted}
            />
          </div>
          <div className="absolute inset-x-0 top-full mt-2">
            <ChatSuggestions
              suggestions={suggestions}
              onSelect={draftSuggestion}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
